import React, {
	FC,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import DataGrid, {
	Column,
	DataGridHandle,
	RenderEditCellProps,
	RenderRowProps,
	Row,
} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Popconfirm, Select, Tooltip } from 'antd';
import { ImportField, ImportRow } from './ImportGridTypes';
import { isRowFullyEmpty, validateRows } from './validateRows';

// Row height tuned to fit ~3 lines of wrapped body text (line-height 22px) plus
// vertical padding, so multi-line cell content is visible without scrolling
// within the cell for typical inputs.
const GRID_ROW_HEIGHT = 72;

// Data columns must carry an explicit pixel width. With no width, react-data-grid
// falls back to CSS `auto`, which makes every column flex to fill the grid. During
// a drag rdg pins only the dragged column to pixels and leaves the rest flexing, so
// the freed/consumed space is redistributed across the *other* auto columns — the
// ones to the LEFT of the handle included. That slides the dragged column's left
// edge while rdg recomputes `newWidth = pointerX - left` on every pointermove, so a
// shrinking `left` inflates the width, which steals more space, which shrinks `left`
// further: a feedback loop that reads as the column ballooning uncontrollably.
// Fixed widths remove the flex entirely (Excel/Google Sheets model): resizing one
// column shifts the rest and leaves trailing space / horizontal scroll instead of
// reflowing them, so the drag tracks the pointer 1:1.
const MIN_COLUMN_WIDTH = 120;

// Content-aware base width: roomy enough for the header label, clamped so very
// long labels don't open absurdly wide. This is the *starting* width — when the
// columns don't fill the grid they're scaled up proportionally to absorb the slack
// (see the fill logic in the `columns` memo) and the result is assigned as a FIXED
// pixel width, preserving the autofit-then-fixed, drag-tracks-1:1 behaviour above.
const getColumnBaseWidth = (label: string): number =>
	Math.max(MIN_COLUMN_WIDTH, Math.min(label.length * 8 + 48, 400));

// Combined width of the non-data frame columns: frozen S.No (60) + delete (40).
// Subtracted from the grid's viewport width to find the space the data columns
// should share when filling.
const FRAME_COLUMNS_WIDTH = 100;

interface CopiedCell {
	value: any;
	columnKey: string;
}

interface EditableImportGridProps {
	fields: ImportField[];
	rows: ImportRow[];
	onRowsChange: (rows: ImportRow[]) => void;
	recordHook?: (record: any) => any;
}

const SelectEditor = ({ row, column, onRowChange, onClose, options }: any) => (
	<div className="import-grid-enum-editor">
		<Select
			autoFocus
			defaultOpen
			showSearch
			bordered={false}
			value={row[column.key] !== undefined && row[column.key] !== '' ? row[column.key] : undefined}
			placeholder="Search..."
			filterOption={(input, option) =>
				String((option && option.children) || '')
					.toLowerCase()
					.includes(input.toLowerCase())
			}
			onChange={(val) => onRowChange({ ...row, [column.key]: val }, true)}
			onBlur={() => onClose(true)}
			dropdownStyle={{ zIndex: 2000 }}
		>
			{options.map((option) => (
				<Select.Option key={option.value} value={option.value}>
					{option.label}
				</Select.Option>
			))}
		</Select>
	</div>
);

const NumberEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<ImportRow>) => (
	<input
		autoFocus
		type="number"
		className="rdg-text-editor h-full w-full border-none outline-none"
		value={row[column.key] !== undefined ? row[column.key] : ''}
		onChange={(event) => {
			const val = event.target.value;
			onRowChange({ ...row, [column.key]: val === '' ? '' : Number(val) });
		}}
		onBlur={() => onClose(true)}
	/>
);

// Multiline-friendly text editor. Enter commits the edit (matches the other
// editors and the Tab/Enter commit flow rdg expects); Shift+Enter inserts a
// newline so users can author wrapped, multi-paragraph values. Mirrors the
// Sheets / Notion convention for textarea-style cells.
const MultilineTextEditor = ({
	row,
	column,
	onRowChange,
	onClose,
}: RenderEditCellProps<ImportRow>) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	// Size the textarea to its content rather than the full cell height. That
	// keeps the editor only as tall as the text, so the cell's `align-content:
	// center` vertically centers it exactly like the read-only content span —
	// the text stays put on entering edit mode instead of jumping to the top.
	const autoSizeTextarea = (textarea: HTMLTextAreaElement) => {
		textarea.style.height = 'auto';
		textarea.style.height = `${textarea.scrollHeight}px`;
	};
	// Focus + caret-to-end synchronously after mount so the user can keep
	// typing onto the existing value rather than at position 0. useLayoutEffect
	// avoids the visible blink that a post-paint useEffect would cause.
	useLayoutEffect(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;
		autoSizeTextarea(textarea);
		textarea.focus();
		const caretPosition = textarea.value.length;
		textarea.setSelectionRange(caretPosition, caretPosition);
	}, []);
	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		autoSizeTextarea(event.target);
		onRowChange({ ...row, [column.key]: event.target.value });
	};
	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			onClose(true);
		}
	};
	const handleBlur = () => onClose(true);
	const initialValue =
		row[column.key] === undefined || row[column.key] === null ? '' : String(row[column.key]);
	return (
		<textarea
			ref={textareaRef}
			className="import-grid-multiline-editor"
			rows={1}
			defaultValue={initialValue}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onBlur={handleBlur}
		/>
	);
};

const CheckboxFormatter = ({ row, column, onRowChange }: any) => (
	<input
		type="checkbox"
		checked={!!row[column.key]}
		onChange={(event) => onRowChange({ ...row, [column.key]: event.target.checked })}
		className="m-auto block"
	/>
);

// Renders the cell value with a native `title` so hovering a truncated cell
// reveals the full content. Skips the title attribute when the value is empty
// so the browser doesn't show an empty bubble.
const CellTextContent: FC<{ value: any }> = ({ value }) => {
	const display = value === undefined || value === null || value === '' ? '' : String(value);
	if (display === '') {
		return <span className="import-grid-cell-content" />;
	}
	return (
		<span className="import-grid-cell-content" title={display}>
			{display}
		</span>
	);
};

const CellWithErrorTooltip = ({
	field,
	row,
	children,
}: {
	field: ImportField;
	row: ImportRow;
	children: React.ReactNode;
}) => {
	// Controlled so a click inside the cell can dismiss the tooltip. Without
	// this, opening an overlay (e.g. a custom-cell modal) covers the cell
	// without ever firing `mouseleave` on the anchor, so the popup hangs around
	// on top of the modal until the cursor physically leaves the cell.
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const errs = row._errors && row._errors[field.key];
	if (errs && errs.length > 0) {
		const handleMouseDown = () => setTooltipVisible(false);
		return (
			<Tooltip
				title={errs.join(', ')}
				color="#ff4d4f"
				visible={tooltipVisible}
				onVisibleChange={setTooltipVisible}
			>
				<div className="h-full w-full" onMouseDown={handleMouseDown}>
					{children}
				</div>
			</Tooltip>
		);
	}
	return <>{children}</>;
};

const rowHasErrors = (row: ImportRow): boolean => {
	if (!row._errors) return false;
	return Object.values(row._errors).some((errors) => errors && errors.length > 0);
};

interface RowInsertSeparatorProps {
	rowIdx: number;
	rowId: number;
	onInsertRowAfter: (rowId: number) => void;
}

// Full-width hover zone straddling a row's bottom border. It's injected as an
// absolutely-positioned sibling of the row (via the grid's renderRow renderer)
// so it scrolls with the rows. Hovering anywhere along it highlights the
// separator line and reveals the insert-row button; clicking the button inserts
// a new row directly below this one. Vertical position is derived from the
// header height (exposed by react-data-grid as --rdg-header-row-height) plus the
// fixed row height, so it lands exactly on the boundary below row `rowIdx`.
const RowInsertSeparator: FC<RowInsertSeparatorProps> = ({ rowIdx, rowId, onInsertRowAfter }) => {
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onInsertRowAfter(rowId);
		},
		[rowId, onInsertRowAfter]
	);
	return (
		<div
			className="import-grid-row-separator"
			style={{
				top: `calc(var(--rdg-header-row-height, 35px) + ${(rowIdx + 1) * GRID_ROW_HEIGHT}px)`,
			}}
		>
			<button
				type="button"
				className="import-grid-insert-row-btn"
				onClick={handleClick}
				aria-label="Insert row below"
				title="Insert row below"
			>
				<PlusOutlined translate="" />
			</button>
		</div>
	);
};

const EditableImportGrid: FC<EditableImportGridProps> = ({
	fields,
	rows,
	onRowsChange,
	recordHook,
}) => {
	const [showErrorsOnly, setShowErrorsOnly] = useState(false);

	// react-data-grid (7.0.0-beta.47) exposes no resize start/end event or
	// "resizing" class — only a continuous onColumnResize(idx, width) during the
	// drag. Track the column being resized so we can paint a full-height black
	// separator across that whole column (header + every row) instead of just the
	// header handle.
	const [resizingColIdx, setResizingColIdx] = useState<number | null>(null);

	// Whether the grid's columns overflow its viewport. Drives the frozen S.No
	// column's drop shadow: hidden when everything fits (the shadow against a
	// flush edge just looks like grime), shown only once content can scroll
	// underneath it — the standard "sticky column casts a shadow over scrolled
	// content" affordance.
	const [isHorizontallyScrollable, setIsHorizontallyScrollable] = useState(false);

	// The grid viewport's current pixel width, used to distribute available space
	// across the data columns so they fill it instead of leaving trailing
	// whitespace. Seeded and kept current by the layout effect below (mount +
	// ResizeObserver). `null` until first measured — columns fall back to their
	// content-aware base widths for that initial frame.
	const [gridWidth, setGridWidth] = useState<number | null>(null);

	// Clear the highlight when the drag ends. `pointerup` alone is not enough:
	// rdg releases its pointer capture immediately after, and its
	// `lostpointercapture` handler runs one final onPointerMove → onColumnResize
	// when the end position differs from the last move (bundle.js:1411-1414). That
	// fires AFTER our pointerup and re-sets resizingColIdx, so the line lingered.
	// `lostpointercapture` bubbles, so handling it here runs in the bubble phase —
	// after rdg's target-phase re-set — and React batches both within that one
	// native event, letting the final null win. pointercancel covers aborted drags.
	useEffect(() => {
		const clearResizing = () => setResizingColIdx((prev) => (prev === null ? prev : null));
		window.addEventListener('pointerup', clearResizing);
		window.addEventListener('lostpointercapture', clearResizing);
		window.addEventListener('pointercancel', clearResizing);
		return () => {
			window.removeEventListener('pointerup', clearResizing);
			window.removeEventListener('lostpointercapture', clearResizing);
			window.removeEventListener('pointercancel', clearResizing);
		};
	}, []);

	const runValidation = useCallback(
		(updatedRows: ImportRow[]): ImportRow[] => validateRows(updatedRows, fields, recordHook),
		[fields, recordHook]
	);

	const handleRowsChange = useCallback(
		(newRows: ImportRow[]) => {
			if (!showErrorsOnly) {
				onRowsChange(runValidation(newRows));
				return;
			}
			// react-data-grid hands us back the FILTERED set with edits applied.
			// Merge those edits back into the full row list by `_rowId` so hidden
			// rows aren't dropped.
			const updatesById = new Map<number, ImportRow>();
			newRows.forEach((row) => updatesById.set(row._rowId, row));
			const merged = rows.map((row) => updatesById.get(row._rowId) || row);
			onRowsChange(runValidation(merged));
		},
		[onRowsChange, runValidation, rows, showErrorsOnly]
	);

	const handleToggleShowErrorsOnly = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setShowErrorsOnly(event.target.checked);
	}, []);

	const buildEmptyRow = useCallback((): ImportRow => {
		const newRow: ImportRow = {
			_rowId: Date.now() + Math.random(),
			_errors: {},
		};
		fields.forEach((field) => {
			newRow[field.key] = field.type === 'boolean' ? false : '';
		});
		return newRow;
	}, [fields]);

	// Imperative handle on the grid so we can scroll the freshly added row into
	// view. Set when a row is appended and consumed by the layout effect below,
	// which fires once the new row has actually rendered.
	const gridRef = useRef<DataGridHandle>(null);
	const scrollToBottomRef = useRef(false);

	// Compare the scroll container's content width against its visible width to
	// know whether the columns can scroll horizontally. Reads layout, so it runs
	// on mount, on container resize (ResizeObserver below), and after a column
	// drag-resize — never on every render.
	const measureHorizontalScroll = useCallback(() => {
		const element = gridRef.current && gridRef.current.element;
		if (!element) return;
		const scrollable = element.scrollWidth > element.clientWidth;
		setIsHorizontallyScrollable((prev) => (prev === scrollable ? prev : scrollable));
	}, []);

	// Read the grid viewport's visible width (clientWidth already excludes any
	// vertical scrollbar) so the column memo can stretch the data columns to fill
	// it. Equality-guarded so an unchanged width doesn't churn the columns memo.
	const measureGridWidth = useCallback(() => {
		const element = gridRef.current && gridRef.current.element;
		if (!element) return;
		const width = element.clientWidth;
		setGridWidth((prev) => (prev === width ? prev : width));
	}, []);

	const handleColumnResize = useCallback(
		(idx: number) => {
			setResizingColIdx((prev) => (prev === idx ? prev : idx));
			// Widening a column can push the total past the viewport; re-measure on
			// the next frame, after rdg has applied the new track width.
			requestAnimationFrame(measureHorizontalScroll);
		},
		[measureHorizontalScroll]
	);

	// Track horizontal scrollability for the frozen-column shadow. ResizeObserver
	// catches container width changes (window resize, panel collapse); the mount
	// pass seeds the initial state. Column drag-resizes are handled in
	// handleColumnResize since they change content width without resizing the box.
	// useLayoutEffect (not useEffect) so the first width measurement lands before
	// paint — the columns fill the grid on the initial frame instead of flashing
	// at their narrow base widths and then snapping wide.
	useLayoutEffect(() => {
		const element = gridRef.current && gridRef.current.element;
		const measure = () => {
			measureGridWidth();
			measureHorizontalScroll();
		};
		if (!element || typeof ResizeObserver === 'undefined') {
			measure();
			return undefined;
		}
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		measure();
		return () => observer.disconnect();
	}, [measureGridWidth, measureHorizontalScroll, fields]);

	const handleAddRow = useCallback(() => {
		const updated = [...rows, buildEmptyRow()];
		// The new row carries no errors, so the "show errors only" filter would
		// hide it — reintroducing the very "did my click do anything?" confusion
		// we're fixing. Drop the filter so the appended row is always visible.
		setShowErrorsOnly(false);
		scrollToBottomRef.current = true;
		onRowsChange(runValidation(updated));
		message.success('Row added');
	}, [buildEmptyRow, rows, onRowsChange, runValidation]);

	const handleDeleteRow = useCallback(
		(rowId: number) => {
			const updated = rows.filter((row) => row._rowId !== rowId);
			onRowsChange(runValidation(updated));
		},
		[rows, onRowsChange, runValidation]
	);

	const handleInsertRowAfter = useCallback(
		(rowId: number) => {
			const insertAt = rows.findIndex((existingRow) => existingRow._rowId === rowId);
			const newRow = buildEmptyRow();
			const updated =
				insertAt === -1
					? [...rows, newRow]
					: [...rows.slice(0, insertAt + 1), newRow, ...rows.slice(insertAt + 1)];
			onRowsChange(runValidation(updated));
		},
		[buildEmptyRow, rows, onRowsChange, runValidation]
	);

	// Single shared slot for the most recently copied cell. Stored in a ref so
	// the keydown handler can read fresh values without re-registering on every
	// rows change, and so copy/paste survives across react-data-grid's internal
	// state transitions (which it does not — it has no notion of our custom
	// cells, so we own the clipboard model entirely).
	const copiedCellRef = useRef<CopiedCell | null>(null);

	const originalIndexById = useMemo(() => {
		const map = new Map<number, number>();
		rows.forEach((row, index) => map.set(row._rowId, index));
		return map;
	}, [rows]);

	const columns: Column<ImportRow>[] = useMemo(() => {
		const snoCol: Column<ImportRow> = {
			key: '_sno',
			name: 'S.No.',
			width: 60,
			resizable: false,
			frozen: true,
			cellClass: 'import-grid-sno-cell',
			renderCell: ({ row }) => {
				const originalIndex = originalIndexById.get(row._rowId);
				return (
					<div className="import-grid-sno-content">
						<span>{(originalIndex !== undefined ? originalIndex : 0) + 1}</span>
					</div>
				);
			},
		};

		// Fill the grid: each data column starts from its content-aware base width;
		// when those bases don't span the viewport we scale them up proportionally
		// so the columns consume the available space rather than leaving a gap on
		// the right. The scaled value is assigned as a FIXED pixel width — that's
		// what keeps drag-resize tracking the pointer 1:1 (see MIN_COLUMN_WIDTH).
		// When the bases already overflow (`fillScale` stays 1) we leave them be and
		// let the grid scroll horizontally. rdg keys manual resizes by column key,
		// so a user's drag survives any later refill — only untouched columns move.
		const baseWidths = fields.map((field) => getColumnBaseWidth(field.label));
		const totalBaseWidth = baseWidths.reduce((total, width) => total + width, 0);
		// 2px slack absorbs per-column floor() rounding so the filled total never
		// nudges one pixel past the viewport and spuriously triggers a scrollbar.
		const availableForData = gridWidth !== null ? gridWidth - FRAME_COLUMNS_WIDTH - 2 : null;
		const fillScale =
			availableForData !== null && totalBaseWidth > 0 && availableForData > totalBaseWidth
				? availableForData / totalBaseWidth
				: 1;

		const dataCols: Column<ImportRow>[] = fields.map((field, index) => {
			const base: Column<ImportRow> = {
				key: field.key,
				name: field.label,
				resizable: true,
				width: Math.floor(baseWidths[index] * fillScale),
				minWidth: MIN_COLUMN_WIDTH,
				cellClass: (row) => {
					const errs = row._errors && row._errors[field.key];
					return errs && errs.length > 0 ? 'import-grid-cell-error' : undefined;
				},
			};

			if (field.renderCustomCell) {
				const customRenderer = field.renderCustomCell;
				return {
					...base,
					editable: false,
					renderCell: ({ row }) => {
						const handleCustomCellChange = (nextValue: any) => {
							const updatedRows = rows.map((existingRow) =>
								existingRow._rowId === row._rowId
									? { ...existingRow, [field.key]: nextValue }
									: existingRow
							);
							handleRowsChange(updatedRows);
						};
						return (
							<CellWithErrorTooltip field={field} row={row}>
								{customRenderer({
									value: row[field.key],
									row,
									field,
									onChange: handleCustomCellChange,
								})}
							</CellWithErrorTooltip>
						);
					},
				};
			}

			if (field.readonly) {
				return {
					...base,
					editable: false,
					cellClass: (row) => {
						const errClass =
							row._errors && row._errors[field.key] && row._errors[field.key].length > 0
								? 'import-grid-cell-error'
								: '';
						return ('import-grid-cell-readonly ' + errClass).trim();
					},
					renderCell: ({ row }) => (
						<CellWithErrorTooltip field={field} row={row}>
							<CellTextContent value={row[field.key]} />
						</CellWithErrorTooltip>
					),
				};
			}

			if (field.type === 'enum' && field.config && field.config.options) {
				const options = field.config.options;
				return {
					...base,
					editable: true,
					renderEditCell: (props) => <SelectEditor {...props} options={options} />,
					renderCell: ({ row }) => {
						const val = row[field.key];
						const matched = options.find((option) => String(option.value) === String(val));
						const displayValue = matched ? matched.label : val;
						return (
							<CellWithErrorTooltip field={field} row={row}>
								<div className="import-grid-enum-cell">
									<CellTextContent value={displayValue} />
									<DownOutlined translate="" className="import-grid-enum-chevron" />
								</div>
							</CellWithErrorTooltip>
						);
					},
				};
			}

			if (field.type === 'boolean') {
				return {
					...base,
					renderCell: (props) => (
						<CellWithErrorTooltip field={field} row={props.row}>
							<CheckboxFormatter {...props} />
						</CellWithErrorTooltip>
					),
				};
			}

			if (field.type === 'number') {
				return {
					...base,
					editable: true,
					renderEditCell: NumberEditor,
					renderCell: ({ row }) => (
						<CellWithErrorTooltip field={field} row={row}>
							<CellTextContent value={row[field.key]} />
						</CellWithErrorTooltip>
					),
				};
			}

			return {
				...base,
				editable: true,
				renderEditCell: MultilineTextEditor,
				renderCell: ({ row }) => (
					<CellWithErrorTooltip field={field} row={row}>
						<CellTextContent value={row[field.key]} />
					</CellWithErrorTooltip>
				),
			};
		});

		const deleteCol: Column<ImportRow> = {
			key: '_delete',
			name: '',
			width: 40,
			resizable: false,
			cellClass: 'import-grid-delete-cell',
			renderCell: ({ row }) => {
				const handleDeleteClick = () => handleDeleteRow(row._rowId);
				const deleteButton = (
					<Button
						type="link"
						size="small"
						className="text-character-secondary"
						icon={<DeleteOutlined translate="" />}
						aria-label="Delete row"
						title="Delete row"
					/>
				);
				// A blank, never-touched row holds nothing worth confirming — wire the
				// click straight to delete. Rows the user has filled in get an inline
				// Popconfirm anchored to the trash icon instead of a blocking dialog.
				if (isRowFullyEmpty(row, fields)) {
					return React.cloneElement(deleteButton, { onClick: handleDeleteClick });
				}
				return (
					<Popconfirm
						title="Delete this row?"
						okText="Delete"
						okType="danger"
						cancelText="Cancel"
						placement="left"
						onConfirm={handleDeleteClick}
					>
						{deleteButton}
					</Popconfirm>
				);
			},
		};

		return [snoCol, ...dataCols, deleteCol];
	}, [fields, handleDeleteRow, originalIndexById, rows, handleRowsChange, gridWidth]);

	// Render each row alongside a full-width insert-row separator beneath it.
	const renderRow = useCallback(
		(key: React.Key, props: RenderRowProps<ImportRow>) => (
			<React.Fragment key={key}>
				<Row {...props} />
				<RowInsertSeparator
					rowIdx={props.rowIdx}
					rowId={props.row._rowId}
					onInsertRowAfter={handleInsertRowAfter}
				/>
			</React.Fragment>
		),
		[handleInsertRowAfter]
	);

	const gridRenderers = useMemo(() => ({ renderRow }), [renderRow]);

	// Single click defaults to SELECT mode (no EDIT) so Cmd+C can fire
	// `onCellKeyDown` below. Edit mode is entered via double-click, Enter, or
	// typing — same contract as Excel / Google Sheets. Exception: enum cells
	// are dropdown-shaped (chevron indicator), so single click also enters
	// EDIT mode for those, matching the original Flatfile importer where the
	// dropdown opens on the first click. Trade-off: copy from a dropdown cell
	// requires pressing Escape first to leave EDIT mode.
	const editOnClickKeys = useMemo(() => {
		const keys = new Set<string>();
		fields.forEach((field) => {
			if (
				field.type === 'enum' &&
				!field.readonly &&
				!field.renderCustomCell &&
				field.config &&
				field.config.options
			) {
				keys.add(field.key);
			}
		});
		return keys;
	}, [fields]);

	// Tracks the cell that received the previous click so a second click on the
	// same already-selected cell can enter EDIT mode — matches the "click to
	// select, click again to rename" pattern users expect from spreadsheet /
	// file-manager UIs. Reset whenever a different cell is clicked.
	const lastClickedCellRef = useRef<{ rowId: number; columnKey: string } | null>(null);

	const handleCellClick = useCallback(
		(args) => {
			if (editOnClickKeys.has(args.column.key)) {
				args.selectCell(true);
				lastClickedCellRef.current = null;
				return;
			}
			// Skip frame cells — they have their own buttons and aren't
			// editable, so "click to edit" doesn't apply.
			if (args.column.key === '_sno' || args.column.key === '_delete') {
				lastClickedCellRef.current = null;
				return;
			}
			const previous = lastClickedCellRef.current;
			const isSameCell =
				previous && previous.rowId === args.row._rowId && previous.columnKey === args.column.key;
			if (isSameCell) {
				args.selectCell(true);
				lastClickedCellRef.current = null;
				return;
			}
			lastClickedCellRef.current = {
				rowId: args.row._rowId,
				columnKey: args.column.key,
			};
		},
		[editOnClickKeys]
	);

	const handleCellKeyDown = useCallback(
		(args, event) => {
			// Owning copy/paste ourselves — instead of relying on rdg's
			// onCopy/onPaste props — lets us treat custom cells
			// (editable: false) and dropdowns the same as text/number cells,
			// which is the parity gap the user reported. But rdg fires this in
			// BOTH select and edit mode, so bail in EDIT mode: when the user is
			// inside the textarea/input editor, Cmd+C/Cmd+V must hit the native
			// field (copy the selected text, paste at the caret) rather than our
			// whole-cell clipboard model.
			if (args.mode === 'EDIT') return;
			if (args.column.key === '_sno' || args.column.key === '_delete') return;
			const isMetaOrCtrl = event.ctrlKey || event.metaKey;
			if (!isMetaOrCtrl) return;
			const key = event.key.toLowerCase();

			if (key === 'c') {
				// Skip when the user has an active text selection in the page —
				// they're copying text, not the cell. Mirrors rdg's own guard.
				const selection = window.getSelection();
				if (selection && !selection.isCollapsed) return;

				const sourceField = fields.find((field) => field.key === args.column.key);
				if (!sourceField) return;
				const value = args.row[args.column.key];
				copiedCellRef.current = { value, columnKey: args.column.key };

				// Best-effort mirror to the system clipboard for cross-app
				// copies — silently no-op when blocked (insecure context,
				// permission denial, IE-era browsers).
				try {
					const text =
						value === undefined || value === null
							? ''
							: typeof value === 'object'
							? JSON.stringify(value)
							: String(value);
					if (navigator.clipboard && navigator.clipboard.writeText) {
						navigator.clipboard.writeText(text).catch(() => undefined);
					}
				} catch (clipboardErr) {
					// Ignore — the in-memory ref above is enough for intra-grid paste.
				}
				message.success('Copied');
				event.preventGridDefault();
				return;
			}

			if (key === 'v') {
				if (!copiedCellRef.current) return;
				const targetField = fields.find((field) => field.key === args.column.key);
				if (!targetField || targetField.readonly) return;

				const updatedRow: ImportRow = {
					...args.row,
					[args.column.key]: copiedCellRef.current.value,
				};
				const updatedRows = rows.map((existingRow) =>
					existingRow._rowId === args.row._rowId ? updatedRow : existingRow
				);
				handleRowsChange(updatedRows);
				event.preventGridDefault();
			}
		},
		[fields, rows, handleRowsChange]
	);

	const errorRowCount = useMemo(
		() => rows.reduce((count, row) => count + (rowHasErrors(row) ? 1 : 0), 0),
		[rows]
	);

	// Treat the "errors only" filter as inactive whenever there are no errors
	// to show, so the grid never goes silently empty after the user finishes
	// fixing them. Computed during render — not stored — so toggling back on
	// is instantaneous when new errors appear, and there is no frame where
	// `displayRows` is an empty filtered set waiting on an effect to reset.
	const effectiveShowErrorsOnly = showErrorsOnly && errorRowCount > 0;

	const displayRows = useMemo(
		() => (effectiveShowErrorsOnly ? rows.filter(rowHasErrors) : rows),
		[rows, effectiveShowErrorsOnly]
	);

	// After Add Row appends to the list (and the new row has rendered), scroll it
	// into view. Keyed on displayRows.length so it runs on the commit that grows
	// the grid; the ref gate ensures only an explicit add triggers a scroll, not
	// every row change (edits, deletes, filter toggles).
	useLayoutEffect(() => {
		if (!scrollToBottomRef.current) return;
		scrollToBottomRef.current = false;
		if (gridRef.current && displayRows.length > 0) {
			gridRef.current.scrollToCell({ rowIdx: displayRows.length - 1 });
		}
	}, [displayRows.length]);

	const getRowClass = useCallback(
		(_row: ImportRow, rowIdx: number) => (rowIdx % 2 === 1 ? 'import-grid-row-alt' : undefined),
		[]
	);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="mb-2 flex items-center justify-between">
				<div className="flex items-center">
					{errorRowCount > 0 && (
						<span className="text-character-danger">
							{errorRowCount} row{errorRowCount !== 1 ? 's' : ''} with errors
						</span>
					)}
					<span className="ml-3 text-[#999]">
						{effectiveShowErrorsOnly
							? `${displayRows.length} of ${rows.length} row${rows.length !== 1 ? 's' : ''}`
							: `${rows.length} row${rows.length !== 1 ? 's' : ''}`}
					</span>
					<label
						className={`ml-3 flex items-center gap-1 whitespace-nowrap ${
							errorRowCount > 0 ? 'cursor-pointer' : 'cursor-not-allowed text-[#bbb]'
						}`}
						title={errorRowCount > 0 ? 'Filter to rows with errors only' : 'No rows have errors'}
					>
						<input
							type="checkbox"
							checked={effectiveShowErrorsOnly}
							disabled={errorRowCount === 0}
							onChange={handleToggleShowErrorsOnly}
						/>
						Show errors only
					</label>
				</div>
				<Button icon={<PlusOutlined translate="" />} onClick={handleAddRow}>
					Add Row
				</Button>
			</div>
			<DataGrid
				ref={gridRef}
				columns={columns}
				rows={displayRows}
				rowKeyGetter={(row) => row._rowId}
				onRowsChange={handleRowsChange}
				onCellClick={handleCellClick}
				onCellKeyDown={handleCellKeyDown}
				onColumnResize={handleColumnResize}
				rowHeight={GRID_ROW_HEIGHT}
				rowClass={getRowClass}
				renderers={gridRenderers}
				className={`import-grid min-h-0 flex-1${
					isHorizontallyScrollable ? ' import-grid-x-scroll' : ''
				}`}
			/>
			{/* Full-height resize indicator: while a column is being dragged, paint a
			    solid black line down its right edge across the header and every row.
			    rdg sets aria-colindex = column.idx + 1 on both header and body cells,
			    so one selector covers the whole column. */}
			{resizingColIdx !== null ? (
				<style>{`
					.import-grid [aria-colindex="${resizingColIdx + 1}"] {
						box-shadow: inset -2px 0 0 0 #000;
					}
				`}</style>
			) : null}
			<style>{`
				.import-grid {
					--rdg-background-color: #fff;
					--rdg-header-background-color: #fafafa;
					--rdg-row-hover-background-color: #f5f5f5;
					--rdg-color: #262626;
					--rdg-font-size: 14px;
					--rdg-color-scheme: light;
					/* Lighter column/row separators than rdg's default #ddd — matches
					   Ant Design's split-line tone. */
					--rdg-border-color: #f0f0f0;
					/* No shadow on the frozen S.No column by default — with nothing
					   scrolled underneath it the shadow just reads as a smudge against
					   a flush edge. Restored below only when the grid can scroll
					   horizontally. */
					--rdg-cell-frozen-box-shadow: none;
				}
				/* Re-enable rdg's frozen-column shadow only once columns overflow the
				   viewport, so the sticky S.No column casts a shadow over the content
				   scrolling beneath it. */
				.import-grid.import-grid-x-scroll {
					--rdg-cell-frozen-box-shadow: 2px 0 5px -2px rgba(136, 136, 136, 0.3);
				}
				/* Prod CSS minifier (cssnano mergeRules) merges react-data-grid's
				   resize-handle rule with unrelated header-cell rules, leaking
				   inline-size: 10px and position: absolute onto the header cell
				   itself and clipping header text to a single character. Reset
				   on the header cell; the resize handle (direct child) keeps
				   its absolute 10px sizing. */
				.import-grid [role="columnheader"] {
					inline-size: auto;
					position: sticky;
					cursor: default;
					inset-block-start: 0;
					inset-inline-end: auto;
					inset-block-end: auto;
				}
				/* Same prod-only cssnano fallout as the header reset above, but on
				   the cell in EDIT mode: the flattened resize-handle rule leaks
				   position: absolute / inline-size: 10px / inset: 0 onto the editing
				   cell (rdg adds .rdg-editor-container there). That collapses the
				   enum/text editor to a ~10px sliver pinned to the cell's right edge,
				   so the Ant Select has no width and its dropdown anchors to nothing
				   and never opens. Pin the editing cell back to its grid track. */
				.import-grid .rdg-editor-container {
					position: relative;
					inline-size: auto;
					inset-block-start: auto;
					inset-inline-end: auto;
					inset-block-end: auto;
					overflow: visible;
				}
				.import-grid ::-webkit-scrollbar {
					width: 8px;
					height: 8px;
				}
				.import-grid ::-webkit-scrollbar-track {
					background: #f1f1f1;
				}
				.import-grid ::-webkit-scrollbar-thumb {
					background: #c1c1c1;
					border-radius: 4px;
				}
				.import-grid ::-webkit-scrollbar-thumb:hover {
					background: #a8a8a8;
				}
				.import-grid-cell-readonly {
					background-color: #f5f5f5 !important;
					color: #999 !important;
					cursor: not-allowed;
				}
				.import-grid-cell-error {
					background-color: #fff2f0 !important;
					border-color: #ff4d4f !important;
				}
				/* Zebra striping for alternate rows. Error/readonly cell colors
				   override via !important so this is a safe per-row default. */
				.import-grid .rdg-row.import-grid-row-alt {
					background-color: #fafafa;
				}
				.import-grid-multiline-editor {
					display: block;
					width: 100%;
					/* Auto-grown to content height in JS (see autoSizeTextarea),
					   capped at the cell so the cell's align-content: center keeps the
					   text in the same spot as the read-only state. Horizontal padding
					   matches the cell's so the text doesn't shift sideways either (the
					   editing cell itself has padding: 0). */
					height: auto;
					max-height: 100%;
					padding: 0 8px;
					border: none;
					outline: none;
					resize: none;
					overflow-y: auto;
					font: inherit;
					line-height: 22px;
					/* Transparent so the cell's (zebra-striped) row background shows
					   through, matching the read-only state instead of forcing white. */
					background-color: transparent;
				}
				/* Vertically center the Ant Select inside the cell when editing
				   an enum value. Without this, the Select's ~32px tall selector
				   sits at the top of the 72px cell and the row appears to
				   "jump" on entering edit mode. */
				.import-grid-enum-editor {
					display: flex;
					align-items: center;
					width: 100%;
					height: 100%;
				}
				.import-grid-enum-editor .ant-select {
					width: 100%;
				}
				.import-grid-sno-cell {
					padding: 0 !important;
				}
				/* Center the delete action button both axes within its narrow column
				   instead of letting it hang off the cell's inline padding. */
				.import-grid-delete-cell {
					padding: 0 !important;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.import-grid-sno-content {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
				}
				/* Row-insert separator: a full-width hover zone straddling each row's
				   bottom border, injected as an absolutely-positioned sibling of the
				   row (see RowInsertSeparator). It sits above cells so hovering
				   anywhere along the boundary reveals the line + insert button. */
				.import-grid-row-separator {
					position: absolute;
					left: 0;
					right: 0;
					height: 12px;
					transform: translateY(-50%);
					/* Below react-data-grid's sticky header cells (z-index 2 regular /
					   3 frozen) so a separator scrolled up to the top tucks BEHIND the
					   header instead of floating over it — the opaque header then both
					   hides it and intercepts the click. Still above the body data cells
					   (z-index 0) so the insert button stays visible and clickable while
					   the separator is in view. */
					z-index: 1;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.import-grid-row-separator::before {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					height: 2px;
					background-color: transparent;
					transition: background-color 0.1s ease;
				}
				.import-grid-row-separator:hover::before {
					background-color: #000000;
				}
				.import-grid-insert-row-btn {
					position: relative;
					z-index: 1;
					width: 18px;
					height: 18px;
					padding: 0;
					display: none;
					align-items: center;
					justify-content: center;
					border: 1px solid #000000;
					border-radius: 50%;
					background-color: #fff;
					color: #000000;
					cursor: pointer;
					font-size: 10px;
					line-height: 1;
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
				}
				.import-grid-insert-row-btn:hover {
					background-color: #000000;
					color: #fff;
				}
				.import-grid-row-separator:hover .import-grid-insert-row-btn {
					display: inline-flex;
				}
				/* Multi-line cell content: wrap long values across the available
				   row height. Native title attribute on the wrapper still
				   surfaces the full value on hover when content exceeds the
				   visible area. */
				.import-grid-cell-content {
					display: -webkit-box;
					-webkit-line-clamp: 3;
					-webkit-box-orient: vertical;
					width: 100%;
					white-space: pre-wrap;
					overflow: hidden;
					overflow-wrap: anywhere;
					word-break: break-word;
					text-overflow: ellipsis;
				}
				/* Enum cells render value + chevron to signal "this is a
				   dropdown" — matches the original Flatfile UI so the user
				   doesn't have to discover the interaction. */
				.import-grid-enum-cell {
					display: flex;
					align-items: center;
					justify-content: space-between;
					width: 100%;
					height: 100%;
					gap: 4px;
					cursor: pointer;
				}
				.import-grid-enum-chevron {
					color: rgba(0, 0, 0, 0.45);
					font-size: 10px;
					flex-shrink: 0;
				}
			`}</style>
		</div>
	);
};

export default EditableImportGrid;
